import * as React from "react";

import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../util.cn";

const navigationMenuTriggerStyle = cva(
  `
    group inline-flex h-9 w-max items-center justify-center
    rounded-3 px-4 py-2 text-2 font-regular
    transition-colors
    disabled:pointer-events-none disabled:opacity-50
    focus:outline-none
  `,
  {
    variants: {
      color: {
        accent: `
          data-[active]:bg-accent-3
          data-[state=open]:bg-accent-3
          focus:bg-accent-3 focus:text-accent-12
          hover:bg-accent-3 hover:text-accent-12
        `,
        gray: `
          data-[active]:bg-gray-4
          data-[state=open]:bg-gray-4
          focus:bg-gray-4 focus:text-gray-12
          hover:bg-gray-4 hover:text-gray-12
        `,
      },
    },
    defaultVariants: {
      color: "accent",
    },
  },
);

const NavigationMenuViewport = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, ...props }, ref) => {
  return (
    <div
      className={cn(`
        absolute left-0 top-full flex justify-center
      `)}
    >
      <NavigationMenuPrimitive.Viewport
        className={cn(
          `
            relative mt-1.5
            h-[var(--radix-navigation-menu-viewport-height)]
            w-full origin-top overflow-hidden rounded-3
            bg-panel text-gray-11 shadow-4
            data-[state=closed]:animate-out
            data-[state=closed]:zoom-out-95
            data-[state=open]:animate-in
            data-[state=open]:zoom-in-90
            md:w-[var(--radix-navigation-menu-viewport-width)]
          `,
          className,
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});
NavigationMenuViewport.displayName
  = NavigationMenuPrimitive.Viewport.displayName;

const NavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ children, className, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.Root
      className={cn(
        `
          relative z-10 flex max-w-max flex-1 items-center
          justify-center
        `,
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      <NavigationMenuViewport />
    </NavigationMenuPrimitive.Root>
  );
});
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.List
      className={cn(
        `
          group flex flex-1 list-none items-center
          justify-center space-x-1
        `,
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ children, className, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.Trigger
      className={cn(navigationMenuTriggerStyle(), "group", className)}
      ref={ref}
      {...props}
    >
      {children}
      {" "}
      <ChevronDownIcon
        aria-hidden="true"
        className={`
          relative top-px ml-1 size-3 transition
          duration-300
          group-data-[state=open]:rotate-180
        `}
      />
    </NavigationMenuPrimitive.Trigger>
  );
});
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.Content
      className={cn(
        `
          left-0 top-0 w-full
          data-[motion^=from-]:animate-in
          data-[motion^=from-]:fade-in
          data-[motion^=to-]:animate-out
          data-[motion^=to-]:fade-out
          data-[motion=from-end]:slide-in-from-right-52
          data-[motion=from-start]:slide-in-from-left-52
          data-[motion=to-end]:slide-out-to-right-52
          data-[motion=to-start]:slide-out-to-left-52
          md:absolute md:w-auto
        `,
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.Indicator
      className={cn(
        `
          top-full z-[1] flex h-1.5 items-end justify-center
          overflow-hidden
          data-[state=hidden]:animate-out
          data-[state=hidden]:fade-out
          data-[state=visible]:animate-in
          data-[state=visible]:fade-in
        `,
        className,
      )}
      ref={ref}
      {...props}
    >
      <div
        className={`
          relative top-[60%] size-2 rotate-45 rounded-tl-2
          shadow-2
        `}
      />
    </NavigationMenuPrimitive.Indicator>
  );
});
NavigationMenuIndicator.displayName
  = NavigationMenuPrimitive.Indicator.displayName;

export {
  NavigationMenu as Root,
  NavigationMenuContent as Content,
  NavigationMenuIndicator as Indicator,
  NavigationMenuItem as Item,
  NavigationMenuLink as Link,
  NavigationMenuList as List,
  NavigationMenuTrigger as Trigger,
  NavigationMenuViewport as Viewport,
  navigationMenuTriggerStyle,
};
